<template>
  <div class="flex-column d-flex">
    <v-card rounded="0" width="100%" elevation="0" class="quiz-card">
      <Typography as="p" variant="h-5" class="quiz-question__title mb-0">
        {{ question.content }}{{ !isRequired ? " (optional)" : "" }}
      </Typography>
      <Typography as="p" variant="body-2" class="pt-5 quiz-question__helper mb-0">
        Choose one or more responses from the answers below
      </Typography>
      <v-divider class="mt-5" />
      <v-item-group
        multiple
        v-model="checkedAnswers"
        :class="answerSubmitted ? 'radio-group-disabled' : ''"
      >
        <div v-for="answer in question.LearningQuizAnswers" :key="answer.learningQuizAnswerId">
          <v-hover v-slot="{ hover }">
            <div
              class="answer-box d-flex align-center cursor-pointer my-1"
              :class="{
                'light-grey-background': hover && !answerSubmitted,
                'light-red-background':
                  !isSurveyQuestion &&
                  answerSubmitted &&
                  answer.correctness <= 0 &&
                  checkedAnswers
                    .map((x) => x.learningQuizAnswerId)
                    .includes(answer.learningQuizAnswerId),
                'brand-pale-green-background':
                  !isSurveyQuestion &&
                  answerSubmitted &&
                  correctAnswers
                    .map((x) => x.learningQuizAnswerId)
                    .includes(answer.learningQuizAnswerId),
              }"
              @click="clickAnswer(answer)"
            >
              <div class="d-flex justify-center mx-n8">
                <v-icon
                  :class="{
                    hidden: !answerIconLogic(answer) || isSurveyQuestion,
                  }"
                  :color="answerIconLogic(answer) == 'mdi-check-circle' ? 'brandGreen' : 'red'"
                  class="correct-icon mr-8 ml-n6"
                  >{{ answerIconLogic(answer) || "mdi-close" }}</v-icon
                >

                <v-checkbox
                  :label="answer.content"
                  :value="checkedAnswers.find((x) => x == answer)"
                  :color="answerSubmitted ? 'grey' : 'brandPrimary'"
                  :readonly="answerSubmitted"
                  :disabled="answerSubmitted"
                  @click.stop="clickAnswer(answer)"
                />
              </div>
            </div>
          </v-hover>
        </div>
      </v-item-group>
      <v-divider />

      <div class="d-flex flex-column align-center mt-10">
        <p v-if="answerSubmitted && !isSurveyQuestion" class="text-center">
          Shading indicates correctness, icons indicate which selections you got right
        </p>
        <v-btn
          :color="!inProgram ? 'brandPrimary' : 'brand'"
          :rounded="!inProgram"
          class="white--text"
          width="120"
          depressed
          :loading="postingResponse"
          :disabled="isMissingRequiredValue(checkedAnswers)"
          @click="checkQuestion"
          v-if="answerSubmitted == false"
        >
          {{ computedSubmitButton }}
        </v-btn>

        <v-btn
          :color="!inProgram ? 'brandPrimary' : 'brand'"
          :rounded="!inProgram"
          class="white--text"
          width="120"
          depressed
          @click="goToNextQuestion"
          v-else-if="!isLastQuestion && answerSubmitted == true"
        >
          Next
        </v-btn>

        <v-btn
          :color="!inProgram ? 'brandPrimary' : 'brand'"
          :rounded="!inProgram"
          class="white--text"
          width="120"
          depressed
          @click="completeQuiz"
          v-else-if="isLastQuestion && answerSubmitted == true && !isSurveyQuestion"
        >
          Complete
        </v-btn>
      </div>
    </v-card>
  </div>
</template>

<script>
import Typography from "@/components/generic/Typography.vue";
import quizQuestionResponseMixin from "@/components/learning/QuizQuestion/mixins/quizQuestionResponseMixin";

export default {
  name: "QuizQuestionSelectAllV2",
  mixins: [quizQuestionResponseMixin],
  components: { Typography },
  data() {
    return {
      checkedAnswers: [],
      correctCount: 0,
      answerSubmitted: false,
    };
  },
  methods: {
    clickAnswer(answer) {
      if (this.answerSubmitted) return;
      var index = this.checkedAnswers.findIndex(
        (x) => x.learningQuizAnswerId == answer.learningQuizAnswerId
      );
      if (index != -1) {
        this.checkedAnswers.splice(index, 1);
      } else {
        this.checkedAnswers.push(answer);
      }
    },
    checkQuestion() {
      if (
        this.skipOptionalUnanswered(this.checkedAnswers, {
          onSuccess: () => {
            this.correctCount++;
            this.handleSuccess();
          },
        })
      ) {
        return;
      }

      var selectedAnswers = this.checkedAnswers.map((x) => x.learningQuizAnswerId);
      var correctAnswerIds = this.correctAnswers.map((x) => x.learningQuizAnswerId);
      var answerValues = this.checkedAnswers.sort().map((x) => x.content);

      var correctness = null;
      if (
        this.noCorrectAnswersMarked ||
        !this.isRequired ||
        selectedAnswers.sort().join(",") === correctAnswerIds.sort().join(",")
      ) {
        this.correctCount++;
        correctness = 1;
      } else {
        correctness = 0;
      }

      const quizQuestionResponse = this.buildBaseResponse({
        learningQuizAnswerId: "[" + selectedAnswers.sort().join(", ") + "]",
        value: answerValues.join(", "),
        correctness: this.isSurveyQuestion ? null : correctness,
      });

      this.postResponseAndAdvance([quizQuestionResponse], {
        onSuccess: this.handleSuccess,
        onError: this.handleError,
      });
    },
    handleSuccess() {
      this.answerSubmitted = true;
      if (
        this.isSurveyQuestion ||
        this.noCorrectAnswersMarked ||
        (!this.isRequired && !this.hasValue(this.checkedAnswers))
      ) {
        this.goToNextQuestion();
      }
    },
    handleError() {
      this.answerSubmitted = false;
    },
    goToNextQuestion() {
      if (!this.isLastQuestion) {
        this.checkedAnswers = [];
        this.answerSubmitted = false;
        this.$emit("next-question", this.correctCount);
        this.correctCount = 0;
      } else {
        this.completeQuiz();
      }
    },
    completeQuiz() {
      this.$emit("complete-quiz", this.correctCount);
    },
    answerIconLogic(answer) {
      const checkedIds = this.checkedAnswers.map((x) => x.learningQuizAnswerId);
      const correctIds = this.correctAnswers.map((x) => x.learningQuizAnswerId);
      const wasChecked = checkedIds.includes(answer.learningQuizAnswerId);
      const isCorrect = correctIds.includes(answer.learningQuizAnswerId);

      if (!this.answerSubmitted) return null;
      if (wasChecked && isCorrect) return "mdi-check-circle";
      if (wasChecked !== isCorrect) return "mdi-close-circle";
      return null;
    },
  },
  computed: {
    correctAnswers() {
      if (!this.question || !this.question.LearningQuizAnswers) return [];
      return this.question.LearningQuizAnswers.filter(
        (ans) => ans.correctness > 0 && ans.status == "Active"
      );
    },
  },
};
</script>

<style scoped>
/* Figma: question = Headline/h5, helper = Body/Body 2 (0.6), labels = Body/Body 1 */
.quiz-question__title {
  color: rgba(0, 0, 0, 0.87);
  word-break: normal;
}

.quiz-question__helper {
  color: rgba(0, 0, 0, 0.6);
}

.quiz-card >>> .v-label {
  font-family: "Avenir Book", Helvetica, Arial, sans-serif !important;
  font-size: 16px !important;
  line-height: 1.5em !important;
  color: rgba(0, 0, 0, 0.87) !important;
}

.light-grey-background {
  background-color: var(--v-lightGrey-base);
}
.brand-pale-green-background {
  background-color: var(--v-brandPaleGreen-base);
}
.light-red-background {
  background-color: #fcdeda;
}

/* Container for individual answer box */
.answer-box {
  min-height: 60px;
  /* height: 60px; */
  width: 100%;
  padding: 0 20px 0 20px;
}

.correct-icon {
  transform: scale(1.1);
}

.radio-group-disabled {
  pointer-events: none !important;
}
</style>
